/**
 * @file inward-sla-settings.tsx
 * @module app/modules/inward/components/inward-sla-settings
 * @description Settings > Inward SLA: minutes allowed for each role and action before a
 *   pending action shows as overdue (start inward, Warehouse approval, Finance approval, cost
 *   price acknowledgement). Everyone with Inward access can see them; only a super admin can
 *   change them.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Button, Card, Form, Table } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { error, success } from "../../../../Global/toast";
import { PermissionModule } from "../../../../utils/constants";
import { useAuth } from "../../auth";
import {
  canPerformInwardAction,
  InwardAction,
  isInwardSuperAdmin,
} from "../utils/inward-access";
import {
  checkReminder,
  checkSla,
  REMINDER_DEFAULT_MINUTES,
  InwardSla,
  InwardSlaAction,
  SLA_MAX,
  SLA_MIN,
  SLA_ROWS,
} from "../utils/inward-sla";
import { formatDateTime } from "../utils/inward-timeline";
import { InwardNoAccess } from "./inward-no-access";
import "../styles/inward.css";

type Form_ = Record<InwardSlaAction, string>;

const toForm = (sla: InwardSla): Form_ =>
  Object.fromEntries(SLA_ROWS.map((r) => [r.key, String(sla[r.key])])) as Form_;

const InwardSlaSettings = () => {
  const { currentUser } = useAuth();
  const canView = canPerformInwardAction(currentUser, InwardAction.View);
  const canEdit = isInwardSuperAdmin(currentUser);
  const [values, setValues] = useState<Form_ | null>(null);
  const [meta, setMeta] = useState<{ by?: string; at?: string }>({});
  const [saving, setSaving] = useState(false);
  const [reminder, setReminder] = useState(String(REMINDER_DEFAULT_MINUTES));
  const [soundOn, setSoundOn] = useState(true);

  useEffect(() => {
    if (!canView) return;
    (async () => {
      const response = await new APICallService(
        INWARD.settings,
        {},
        "",
        "",
        "",
        "",
        PermissionModule.Inward
      ).callAPI();
      if (response?.sla) {
        setValues(toForm(response.sla));
        setReminder(String(response.reminderIntervalMinutes ?? REMINDER_DEFAULT_MINUTES));
        setSoundOn(response.reminderSoundEnabled !== false);
        setMeta({ by: response.updatedBy?.name, at: response.updatedAt });
      }
    })();
  }, [canView]);

  if (!canView) return <InwardNoAccess />;
  if (!values) {
    return (
      <div className="d-flex justify-content-center p-10">
        <Loader loading />
      </div>
    );
  }

  const save = async () => {
    const problem = checkSla(values) || checkReminder(reminder);
    if (problem) {
      error(problem);
      return;
    }
    setSaving(true);
    const sla = Object.fromEntries(
      SLA_ROWS.map((r) => [r.key, Number(values[r.key])])
    );
    const response = await new APICallService(
      INWARD.updateSettings,
      { sla, reminderIntervalMinutes: Number(reminder), reminderSoundEnabled: soundOn },
      "",
      "",
      "",
      "",
      PermissionModule.Inward
    ).callAPI();
    setSaving(false);
    if (response?.sla) {
      setValues(toForm(response.sla));
      setReminder(String(response.reminderIntervalMinutes ?? REMINDER_DEFAULT_MINUTES));
      setSoundOn(response.reminderSoundEnabled !== false);
      setMeta({ by: response.updatedBy?.name, at: response.updatedAt });
      success("Inward SLA saved");
    }
  };

  return (
    <Card className="inward-card" data-testid="inward-sla-settings">
      <Card.Body>
        <h2 className="fs-20 fw-bold mb-1">Inward SLA</h2>
        <p className="fs-14 text-muted mb-4">
          Minutes allowed for each action. After that the pending action shows
          as overdue on the dashboard.
          {meta.at
            ? ` Last changed ${formatDateTime(meta.at)}${
                meta.by ? ` by ${meta.by}` : ""
              }.`
            : ""}
        </p>
        <Table className="align-middle fs-15 mb-4">
          <thead>
            <tr>
              <th>Role</th>
              <th>Action</th>
              <th>Clock starts</th>
              <th style={{ width: 160 }}>SLA (minutes)</th>
            </tr>
          </thead>
          <tbody>
            {SLA_ROWS.map((row) => (
              <tr key={row.key} data-testid={`sla-row-${row.key}`}>
                <td className="fw-semibold">{row.role}</td>
                <td>{row.action}</td>
                <td className="text-muted fs-14">{row.from}</td>
                <td>
                  <Form.Control
                    className="inward-input"
                    type="number"
                    inputMode="numeric"
                    min={SLA_MIN}
                    max={SLA_MAX}
                    disabled={!canEdit}
                    value={values[row.key]}
                    onChange={(e) =>
                      setValues((v) => ({
                        ...(v as Form_),
                        [row.key]: e.target.value,
                      }))
                    }
                    aria-label={`${row.action} SLA in minutes`}
                    data-testid={`sla-${row.key}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex flex-wrap align-items-center gap-4 mb-5" data-testid="sla-reminder-row">
          <div style={{ maxWidth: 560 }}>
            <div className="fs-15 fw-semibold">Overdue reminder sound (minutes)</div>
            <div className="fs-14 text-muted">
              While any pending action is overdue, a red bell shows on every page and a ding plays
              at this interval (first ding as soon as something becomes overdue). The switch is for
              super admins only; other users always hear it for the actions their access allows.
            </div>
          </div>
          <Form.Control
            className="inward-input"
            style={{ width: 160 }}
            type="number"
            inputMode="numeric"
            min={SLA_MIN}
            max={SLA_MAX}
            disabled={!canEdit}
            value={reminder}
            onChange={(e) => setReminder(e.target.value)}
            aria-label="Overdue reminder sound interval in minutes"
            data-testid="sla-reminder"
          />
          <Form.Check
            type="switch"
            id="sla-reminder-sound"
            className="fs-15 fw-semibold"
            label={soundOn ? "Super admin sound on" : "Super admin sound off (bell only)"}
            disabled={!canEdit}
            checked={soundOn}
            onChange={(e) => setSoundOn(e.target.checked)}
            data-testid="sla-reminder-sound"
          />
        </div>
        {canEdit ? (
          <Button
            className="inward-touch-btn px-8"
            disabled={saving}
            onClick={save}
            data-testid="sla-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        ) : (
          <div className="fs-14 text-muted" data-testid="sla-read-only">
            Only a super admin can change the SLA.
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default InwardSlaSettings;
